import * as React from 'react';
import { Viewport, ViewSmall, ViewLarge } from '@shared-ui/viewport-context';
import { Footer, Header } from '@gc/global-components';

import { UitkSpacing, UitkSpacingProps } from 'uitk-react-spacing';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkPrimaryButton } from 'uitk-react-button';
import { UitkLayoutFlex } from 'uitk-react-layout-flex';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';

import { useStores } from 'utility/hooks';
import { analyticsConfigMapper, openInSameTab, trackAnalyticsList } from 'utility';
import { NoApiResponse } from 'components/shared/error-messages';
import { ServicingCompleteBookingFailure } from 'components/servicing';

import { ClientSideAnalyticsFragment, GlobalNavigationLineOfBusiness } from '__generated__/api';

interface PrimaryButtonProps {
  buttonText: string;
  clickHandler: (data) => void;
  accessibility: string;
  analytics: ClientSideAnalyticsFragment;
}

const PrimaryButton = ({ buttonText, clickHandler, accessibility, analytics }: PrimaryButtonProps) => {
  const { analytics: analyticsStore } = useStores();

  const trackAnalytics = analytics && analyticsConfigMapper(analytics, analyticsStore);

  return (
    buttonText &&
    clickHandler && (
      <>
        <UitkPrimaryButton onClick={clickHandler} analytics={trackAnalytics}>
          {buttonText}
        </UitkPrimaryButton>
        {accessibility && <span className="is-visually-hidden">{accessibility}</span>}
      </>
    )
  );
};

const mainSpacingProps: UitkSpacingProps = {
  margin: { block: 'twelve' },
  padding: 'twelve'
};
const iconSpacingProps: UitkSpacingProps = { margin: { blockend: 'four' } };
const headingSpacingProps: UitkSpacingProps = { margin: { blockend: 'two' } };
const bodySpacingProps: UitkSpacingProps = { margin: { blockend: 'three' } };

export const ServicingError = () => {
  const { analytics, errorStore } = useStores();
  const { failedServicingMessaging, shouldRenderServicingFailedRequestError } = errorStore;
  const { presentation, primaryAction } = failedServicingMessaging?.emptyState || {};
  const { heading, body, icon, primaryButton } = presentation || {};

  analytics.markPageUsable();

  const onClickAction = React.useCallback(
    (error: ServicingCompleteBookingFailure['emptyState']['primaryAction']) => () => {
      switch (error.__typename) {
        case 'FlightsResourceLinkAction': {
          switch (error.resource?.__typename) {
            case 'HttpURI':
              openInSameTab(error.resource.relativePath);
              break;
            case 'TelURI':
              openInSameTab(error.resource.value);
              break;
          }
          break;
        }
        case 'FlightsRefreshAction': {
          window.location.reload();
          break;
        }
      }
    },
    []
  );

  React.useEffect(() => {
    switch (primaryAction?.__typename) {
      case 'FlightsResourceLinkAction':
      case 'FlightsRefreshAction': {
        trackAnalyticsList(primaryAction.analyticsList, analytics);
        break;
      }
    }
  }, [primaryAction, analytics]);

  return (
    <>
      <Header lineOfBusiness={GlobalNavigationLineOfBusiness.FLIGHT} />
      {shouldRenderServicingFailedRequestError ? (
        <UitkSpacing margin={mainSpacingProps.margin} padding={mainSpacingProps.padding}>
          <main data-test-id="failed-request-servicing">
            <UitkLayoutFlex alignContent="center" justifyContent="center">
              <UitkText align="center">
                {icon && (
                  <UitkSpacing margin={iconSpacingProps.margin}>
                    <UitkIcon name={icon.id} size={UitkIconSize.XLARGE} />
                  </UitkSpacing>
                )}
                <UitkSpacing margin={headingSpacingProps.margin}>
                  <UitkHeading size={5} tag="h1">
                    {heading}
                  </UitkHeading>
                </UitkSpacing>
                <UitkSpacing margin={bodySpacingProps.margin}>
                  <UitkText>{body}</UitkText>
                </UitkSpacing>
                <Viewport>
                  <ViewLarge>
                    <>
                      {primaryAction.__typename === 'FlightsResourceLinkAction' && primaryAction.accessibility ? (
                        <UitkSpacing margin={bodySpacingProps.margin}>
                          <UitkText size={400}>{primaryAction.accessibility}</UitkText>
                        </UitkSpacing>
                      ) : (
                        <PrimaryButton
                          buttonText={primaryButton?.primary}
                          clickHandler={onClickAction(primaryAction)}
                          accessibility={primaryButton?.accessibility}
                          analytics={primaryButton?.analytics}
                        />
                      )}
                    </>
                  </ViewLarge>
                  <ViewSmall>
                    <PrimaryButton
                      buttonText={primaryButton?.primary}
                      clickHandler={onClickAction(primaryAction)}
                      accessibility={primaryButton?.accessibility}
                      analytics={primaryButton?.analytics}
                    />
                  </ViewSmall>
                </Viewport>
              </UitkText>
            </UitkLayoutFlex>
          </main>
        </UitkSpacing>
      ) : (
        <NoApiResponse />
      )}
      <Footer />
    </>
  );
};
